import { CommonModule } from '@angular/common';
import { Component, Input, computed, inject, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { Dialog } from '@capacitor/dialog';
import { IonInput, ModalController } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { CameraButtonComponent, FileUpload, ImageDialogComponent, LoadingComponent, QueueItem } from '@scandium-oy/ngx-scandium';
import { addHours, startOfDay, startOfToday } from 'date-fns';
import { defer, from } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { AttachmentsComponent } from 'src/app/components/attachments/attachments.component';
import { CarouselImage } from 'src/app/components/carousel/images/carousel-image.model';
import { ImageComponent, ImageItem } from 'src/app/components/images/image.component';
import { ImagesComponent } from 'src/app/components/images/images.component';
import { SegmentListComponent } from 'src/app/components/segment-list/segment-list.component';
import { SelectAreaDialogComponent } from 'src/app/components/select-area/select-area.dialog';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SpeechToTextComponent } from 'src/app/components/speech-to-text/speech-to-text.component';
import { TakeVideoComponent } from 'src/app/components/take-video/take-video.component';
import { ConfirmDialogComponent } from 'src/app/confirm-dialog/confirm-dialog.dialog';
import { AreaMap } from 'src/app/models/area-map.model';
import { Contractor } from 'src/app/models/contractor.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { SubItem } from 'src/app/models/sub-item.model';
import { RoomTicket, TicketHistory, TicketNote, TicketStatus } from 'src/app/models/ticket.model';
import { User } from 'src/app/models/user.model';
import { TranslateFieldPipe } from 'src/app/pipes/field-translate/field-translate.pipe';
import { TimestampPipe } from 'src/app/pipes/timestamp/timestamp.pipe';
import { SelectDialogComponent } from 'src/app/select-dialog/select.dialog';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SiteAreasService } from 'src/app/services/site-areas.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { AppAreaMapComponent } from 'src/app/sites/edit-site/floors/area-map.component';
import { AppFabricComponent } from 'src/app/sites/edit-site/floors/fabric.component';
import { ShowPointDialogComponent } from 'src/app/sites/edit-site/floors/show-point.dialog';
import { getThumb } from 'src/app/utility/image';
import { Roles, adminActionRoles, companyAdminRoles, companyAdmins, companyManagers, managerAdminRoles } from 'src/app/utility/role';
import { defaultRoom } from 'src/app/utility/room';
import { fieldSorter, sortAnyByTimestamp } from 'src/app/utility/sort';
import { timeArray } from 'src/app/utility/time';
import { BlockButtonComponent } from './component/block-button.component';
import { AddphotosDialogComponent } from './dialog/add-photos/add-photos.dialog';
import { AnswerDialogComponent } from './dialog/answer/answer.dialog';

interface IHistory {
  text: string;
  textText?: Record<string, string>;
  comment?: string;
  commentText?: Record<string, string>;
  username: string;
  timestamp: Date;
  label?: string;
  labelColor?: string;
  status?: string;
  images?: string[];
  videos?: string[];
  blockedBy?: SubItem;
  userImage?: string;
  creator?: User;
}

@Component({
  selector: 'app-ticket',
  templateUrl: './ticket.component.html',
  styleUrls: ['./ticket.component.scss'],
  providers: [
    TranslateFieldPipe,
  ],
  imports: [
    AppCommonModule,
    CommonModule,
    CameraButtonComponent,
    ImagesComponent,
    TakeVideoComponent,
    LoadingComponent,
    SelectDayComponent,
    AttachmentsComponent,
    BlockButtonComponent,
    TranslateFieldPipe,
    SegmentListComponent,
    ImageComponent,
    TimestampPipe,
    SpeechToTextComponent,
    AppAreaMapComponent,
    AppFabricComponent,
  ],
})
export class TicketComponent {
  private siteAreaService = inject(SiteAreasService);
  private sitesService = inject(SitesService);
  private translate = inject(TranslateService);

  private siteGuidS = signal<string>(null);

  readonly companyAdminRoles = companyAdminRoles;
  readonly companyAdmins = companyAdmins;
  readonly companyManagers = companyManagers;
  readonly adminsAndManagers = [...companyAdmins, ...companyManagers];
  readonly residentsAndAdminsAndManagers = [Roles.resident, ...this.adminsAndManagers];
  readonly managerRoles = managerAdminRoles;

  history = signal<IHistory[]>([]);
  ticket: RoomTicket;
  canDelete = signal(false);
  isCommercial = computed(() => this.site()?.commercial);
  readonly defaultRoom = defaultRoom;

  map = signal<AreaMap>(null);
  imageElement = signal<HTMLImageElement>(null);

  @Input()
  set roomTicket(value: RoomTicket) {
    this.siteGuidS.set(value.site);
    this.ticket = value;
    const currentUser = this.usersService.currentUserS();
    this.canDelete.set(adminActionRoles.includes(currentUser?.role) || value.users?.includes(currentUser?.guid));
    this.updateHistory(value);
    if (value?.images || value?.videos) {
      const images: ImageItem[] = value.images?.map((url) => ({
        image: url, preview$: defer(() => from(getThumb(url))).pipe(
          map((thumb) => thumb?.thumb ?? url),
          shareReplay(1),
        ),
      })) ?? [];
      this.images.set(images);
      const videos: CarouselImage[] = value.videos?.map((url) => ({ url, type: 'video' })) ?? [];
      this.videos.set(videos);
    }
    if (value.location?.length > 0) {
      const url = value.location[0].url;
      this.setMap(url);
    } else if (value.area) {
      this.siteAreaService.getListMap({ site: value.site, area: value.area?.guid ?? null }).pipe(
        map((it) => it.filter((itt) => value.area != null && itt.area === value.area.guid)),
        take(1),
      ).subscribe((maps) => {
        if (maps?.length > 0) {
          this.map.set(maps[0]);
          const site = this.sitesService.getSync(this.siteGuidS());
          const baseMap = site.floors.find((it) => it.name === this.map().floor);
          this.setMap(baseMap.url);
        }
      });
    }
  }

  dismiss = output();
  segment = output<string>();

  readonly pickerColumns = timeArray;

  role = computed(() => this.usersService.currentUserS()?.role);
  user = computed(() => this.usersService.currentUserS());
  private site = computed(() => this.sitesService.getSync(this.siteGuidS()));

  canManage = computed(() => {
    const user = this.user();
    if (this.adminsAndManagers.includes(this.role())) {
      return true;
    }
    if (user.role === Roles.contractorAdmin) {
      const contractor = this.site()?.mainContractors.find((it) => it.guid === user.contractors[0]);
      return contractor.canManage;
    }
    return false;
  });

  readonly statuses = TicketStatus;
  readonly today = startOfToday().toISOString();

  users$ = toObservable(this.user).pipe(
    filter((user) => [Roles.contractorAdmin].includes(user?.role)),
    switchMap((user) => this.usersService.getListFromDb({ contractor: user.contractors[0] })),
    map((users) => users.sort(fieldSorter(['displayName']))),
    map((users) => users.map((u) => ({ guid: u.guid, name: u.displayName, selected: this.ticket.users?.includes(u.guid) }))),
    shareReplay(1),
  );

  segmentValue = signal<string>('main');
  images = signal<ImageItem[]>([]);
  videos = signal<CarouselImage[]>([]);

  segmentButtons = [
    {
      value: 'main',
      name: 'calendar.site.name',
      show: true,
    },
    {
      value: 'attachments',
      name: 'site.attachments',
      show: true,
    },
    {
      value: 'settings',
      name: 'nav.materials.manage',
      show: [...adminActionRoles, Roles.contractorAdmin].includes(this.usersService.currentUserS()?.role),
    },
    {
      value: 'images',
      name: 'site.images',
      show: true,
    },
    /*{
      value: 'productInfo',
      name: 'tickets.productInfo',
      show: true,
    },*/
  ];

  constructor(
    private contractorsService: ContractorsService,
    private modalCtrl: ModalController,
    private translateField: TranslateFieldPipe,
    private usersService: UsersService,
    private workerTicketsService: WorkerTicketsService,
  ) { }

  private getUserImage(username: string) {
    return this.usersService.getByUsername(username)?.image ?? null;
  }

  private updateHistory(ticket: RoomTicket) {
    const ticketHistory: IHistory[] = ticket.history.map((it) => {
      if (it.status === TicketStatus.inProgress && it.backFromBlocked) {
        it.status = 'removedBlocked';
      }
      const translateText = `kanban.logEvent.${it.status}`;
      const text = this.translate.instant(translateText,
        {
          additionalWork: ticket.blockedBy ? this.translate.instant('kanban.logEvent.additionalWork') : '',
        });
      let creator: User;
      if (it.status === TicketStatus.created) {
        creator = this.usersService.getSync(it.userGuid);
      }
      return {
        text,
        timestamp: it.timestamp,
        username: it.username,
        creator,
        userImage: this.getUserImage(it.username),
        comment: it.comment,
        commentText: it.commentText,
        label: [TicketStatus.done, TicketStatus.blocked].includes(it.status as TicketStatus) ? it.status : 'log',
        labelColor: it.status === TicketStatus.done ? 'success' : it.status === TicketStatus.blocked ? 'danger' : 'medium',
        status: it.status,
        images: it.images,
        videos: it.videos,
        blockedBy: it.status === TicketStatus.blocked ? ticket.blockedBy : null,
      };
    }) ?? [];
    const ticketNotes: IHistory[] = ticket.notes?.filter((it) => !it.isAdHoc).map((it) => {
      const text = it.textText != null ? this.translateField.transformInstant(it.textText) : it.text;
      return {
        text: it.title ?? text,
        textText: it.titleText,
        comment: it.title ? it.text : null,
        commentText: it.textText ?? null,
        timestamp: it.timestamp,
        username: it.username,
        userImage: this.getUserImage(it.username),
        label: 'note',
        labelColor: 'light',
      };
    }) ?? [];
    this.history.set([...ticketHistory, ...ticketNotes].sort((a, b) => sortAnyByTimestamp(a, b, 'timestamp')));
  }

  private _save(ticket: RoomTicket) {
    this.updateHistory(ticket);
    this.workerTicketsService.updateRoomTicket(ticket);
  }

  private saveTicket(options: {
    status: TicketStatus;
    comment?: string;
    images?: string[];
    videos?: string[];
    blockedBy?: SubItem;
    onlyImages?: boolean;
    backFromBlocked?: boolean;
  },
  ) {
    if (options.onlyImages) {
      if (options.images?.length > 0) {
        if (this.ticket.images == null) {
          this.ticket.images = [];
        }
        this.ticket.images.push(...options.images);
      }
      if (options.videos?.length > 0) {
        if (this.ticket.videos == null) {
          this.ticket.videos = [];
        }
        this.ticket.videos.push(...options.videos);
      }
      this.workerTicketsService.updateRoomTicket(this.ticket);
      return;
    }
    const username = this.usersService.currentUserS()?.displayName ?? this.ticket.shared?.email;
    let history: TicketHistory;
    if (options.backFromBlocked) {
      history = this.workerTicketsService.createHistory('removedBlocked', options.comment, options.images, options.videos, username);
    } else {
      history = this.workerTicketsService.createHistory(options.status, options.comment, options.images, options.videos, username);
    }
    this.ticket.history.push(history);
    if (options.images?.length > 0) {
      if (this.ticket.images == null) {
        this.ticket.images = [];
      }
      this.ticket.images.push(...options.images);
    }
    if (options.videos?.length > 0) {
      if (this.ticket.videos == null) {
        this.ticket.videos = [];
      }
      this.ticket.videos.push(...options.videos);
    }
    if (options.blockedBy) {
      this.ticket.blockedBy = options.blockedBy;
    }
    if (options.comment == null) {
      const user = this.usersService.currentUserS();
      if (user != null) {
        if (![Roles.superAdmin].includes(user.role)) {
          const displayName = user.displayName;
          if (this.ticket.users?.length > 0) {
            if (!this.ticket.users.includes(user.guid)) {
              this.ticket.users.push(user.guid);
              this.ticket.usernames.push(displayName);
            }
          } else {
            this.ticket.users = [user.guid];
            this.ticket.usernames = [displayName];
          }
        }
      }
    }
    this.ticket.status = options.status;
    this._save(this.ticket);
  }

  private async takePhotos(status: TicketStatus) {
    const contractor = this.contractorsService.contractorS();
    const imageOptions = contractor?.siteOptions?.find((it) => it.site === this.siteGuidS())?.images ?? (this.site().commercial ? 'none' : 'after');
    const required = (status === TicketStatus.inProgress && ['before', 'always'].includes(imageOptions))
      || (status === TicketStatus.done && ['after', 'always'].includes(imageOptions))
      || (status === TicketStatus.blocked && ['always'].includes(imageOptions));

    const cb = (imageUrl: string) => {
      this.saveTicket({ status: null, onlyImages: true, images: [imageUrl] });
    };
    cb.bind(this);
    return this.modalCtrl.create({
      component: AddphotosDialogComponent,
      componentProps: { required, cb, mediaItems: (this.ticket.images?.length ?? 0) + (this.ticket.videos?.length ?? 0) },
      cssClass: ['modal-fullscreen', 'transparent-modal'],
    }).then(async (m) => {
      m.present();

      return m.onDidDismiss<{ images: string[]; videos: string[]; items: QueueItem<FileUpload>[] }>().then((data) => {
        if (data.data?.items.length > 0) {
          return { data: { images: [], videos: [] }, role: data.role };
        }
        return data;
      });
    });
  }

  private setMap(url: string) {
    const imgElement = new Image();
    imgElement.src = url;
    imgElement.width = 475;
    imgElement.height = 375;
    imgElement.crossOrigin = 'anonymous';
    setTimeout(() => {
      this.imageElement.set(imgElement);
    }, 500);
  }

  segmentChanged(value: string) {
    this.segmentValue.set(value);
    this.segment.emit(value);
  }

  onImage(image: string) {
    this.modalCtrl.create({
      component: ImageDialogComponent,
      componentProps: { image, images: this.ticket.images },
      cssClass: ['modal-fullscreen', 'transparent-modal'],
    }).then((modal) => {
      modal.present();
    });
  }

  onMap() {
    this.modalCtrl.create({
      component: ShowPointDialogComponent,
      componentProps: { point: this.map(), imageElement: this.imageElement(), points: this.ticket.location },
      cssClass: ['modal-fullscreen', 'transparent-modal'],
    }).then(async (m) => m.present());
  }

  onInviteByEmail() {
    this.modalCtrl.create({
      component: ConfirmDialogComponent, componentProps: {
        title: this.translate.instant('tickets.assignEmail'),
        text: this.translate.instant('tickets.assingEmailInfo'),
        hasInput: this.translate.instant('tickets.giveEmail'),
        buttons: [this.translate.instant('general.cancel'), this.translate.instant('general.ok')],
      },
    }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.index === 1) {
            const user = this.usersService.currentUserS();
            const email = data.data.value;
            this.ticket.contractor = { guid: email, name: email };
            const expiresDate = addHours(new Date(), 48);
            const expires = Timestamp.fromDate(expiresDate);
            this.ticket.shared = { email, expires };
            const text = this.isCommercial() ? this.translate.instant('tickets.acceptedCommercial', { name: user.displayName, org: this.ticket.contractor.name })
              : this.translate.instant('tickets.accepted', { name: user.displayName, org: this.ticket.contractor.name });
            const history = this.workerTicketsService.createHistory('assigned', text);
            this.ticket.history.push(history);
            this._save(this.ticket);
          }
        }
      });
    });
  }

  onContractor() {
    const user = this.usersService.currentUserS();
    const site = this.sitesService.getSync(this.ticket.site);
    const items = Array<SubItem>();
    site.mainContractors?.map((it) => {
      if (!items.some((itt) => itt.guid === it.guid)) {
        const item = { guid: it.guid, name: it.name };
        items.push(item);
      }
    });
    this.modalCtrl.create({ component: SelectDialogComponent<Contractor>, componentProps: { items } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.ticket.contractor = { guid: data.data.guid, name: data.data.name };
          const text = this.isCommercial() ? this.translate.instant('tickets.acceptedCommercial', { name: user.displayName, org: this.ticket.contractor.name })
            : this.translate.instant('tickets.accepted', { name: user.displayName, org: this.ticket.contractor.name });
          const history = this.workerTicketsService.createHistory('assigned', text);
          this.ticket.history.push(history);
          this._save(this.ticket);
        }
      });
    });
  }

  onAnswer() {
    this.modalCtrl.create({ component: AnswerDialogComponent }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          this.ticket.status = TicketStatus.done;
          this.onNote({ value: data.data }, 'Isännöitsijän vastaus');
        }
      });
    });
  }

  saveAttachment(items: string[]) {
    this.ticket.attachments = items;
    this._save(this.ticket);
  }

  onDuration(value?: number | string) {
    if (value === null) {
      Dialog.prompt({
        title: this.translate.instant('tickets.otherTime'),
        message: this.translate.instant('tickets.otherTimeText'),
      }).then((dialogValue) => {
        if (dialogValue) {
          this.onDuration(+dialogValue.value);
        }
      });
      return;
    }
    if (value == null) {
      value = 0;
    }
    this.ticket.hours = +value;
    this._save(this.ticket);
  }

  onDeadlineChange(value: string) {
    if (value) {
      const date = startOfDay(new Date(value));
      this.ticket.deadline = date;
    } else {
      this.ticket.deadline = null;
    }
    this._save(this.ticket);
  }

  onImageUrl(imageUrl: string) {
    if (!this.ticket.images) {
      this.ticket.images = [];
    }
    this.ticket.images.push(imageUrl);
    const images: ImageItem[] = this.ticket.images.map((url) => ({
      image: url, preview$: defer(() => from(getThumb(url))).pipe(
        map((thumb) => thumb?.thumb ?? url),
        shareReplay(1),
      ),
    })) ?? [];
    this.images.set(images);
    this._save(this.ticket);
  }

  videoUrl(videoUrl: string) {
    if (!this.ticket.videos) {
      this.ticket.videos = [];
    }
    this.ticket.videos.push(videoUrl);
    const videos: CarouselImage[] = this.ticket.videos.map((url) => ({ url, type: 'video' })) ?? [];
    this.videos.set(videos);
    this._save(this.ticket);
  }

  removePaused() {
    this.ticket.status = TicketStatus.inProgress;
    this.ticket.history = this.ticket.history.filter((it) => it.status !== TicketStatus.paused);
    this._save(this.ticket);
  }

  toProgress(ask?: boolean, fromBlocked?: boolean) {
    this.takePhotos(TicketStatus.inProgress).then((ret) => {
      if (ret.role === 'cancel') {
        return;
      }
      const value = ret.data;
      if (ask) {
        Dialog.prompt({
          message: this.translate.instant('kanban.whyBackToProgress'),
        }).then((prompt) => {
          if (prompt.value) {
            this.saveTicket({ status: TicketStatus.inProgress, comment: prompt.value, images: value?.images, videos: value?.videos });
          }
        });
      } else if (fromBlocked) {
        Dialog.prompt({
          message: this.translate.instant('kanban.fromBlocked'),
        }).then((prompt) => {
          const comment = prompt.value;
          this.saveTicket({ status: TicketStatus.inProgress, comment, images: value?.images, videos: value?.videos, backFromBlocked: true });
        });
      } else {
        this.saveTicket({ status: TicketStatus.inProgress, images: value?.images, videos: value?.videos });
      }
    });
  }

  toHandled() {
    this.saveTicket({ status: TicketStatus.done, images: [], videos: [] });
  }

  toDone() {
    this.takePhotos(TicketStatus.done).then((ret) => {
      if (ret.role === 'cancel') {
        return;
      }
      const value = ret.data;
      this.saveTicket({ status: TicketStatus.done, images: value?.images, videos: value?.videos });
    });
  }

  toBacklog() {
    this.saveTicket({ status: TicketStatus.created });
  }

  toBlocked(siteGuid: string, adHocTicket?: RoomTicket) {
    if (adHocTicket) {
      this.workerTicketsService.saveRoomTickets([adHocTicket], { guid: siteGuid } as Site, true);
      this.saveTicket({
        status: TicketStatus.blocked, images: adHocTicket?.images, videos: adHocTicket?.videos, blockedBy: { guid: adHocTicket.guid, name: adHocTicket.name },
      });
    } else {
      Dialog.prompt({
        message: this.translate.instant('kanban.blockDescription'),
      }).then((prompt) => {
        const comment = prompt.value;
        this.takePhotos(TicketStatus.blocked).then((ret) => {
          if (ret.role === 'cancel') {
            return;
          }
          const value = ret.data;
          this.saveTicket({ status: TicketStatus.blocked, images: value?.images, videos: value?.videos, comment });
        });
      });
    }
  }

  onNote(noteInput: { value?: string | number }, title?: string) {
    const text = noteInput.value as string;
    if (text != null && text.length > 0) {
      const user = this.usersService.currentUserS();
      const note: TicketNote = {
        timestamp: new Date(),
        username: user?.displayName,
        userGuid: user?.guid,
        text,
        isAdHoc: false,
        title: title ?? null,
      };
      if (!this.ticket.notes) {
        this.ticket.notes = [];
      }
      this.ticket.notes.push(note);
      noteInput.value = null;
      this._save(this.ticket);
    }
  }

  selectWorker(items: { guid: string; name: string; selected: boolean }[]) {
    const clearButton = this.ticket.users?.length > 0;
    this.modalCtrl.create({ component: SelectDialogComponent<User>, componentProps: { items, clearButton, multiple: true } }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (data.data.clear) {
            this.ticket.users = [];
            this.ticket.usernames = [];
          } else {
            this.ticket.users = data.data.map((u) => u.guid);
            this.ticket.usernames = data.data.map((u) => u.name);
            const history = this.workerTicketsService.createHistory('assigned', data.data.map((u) => u.name)[0], [], []);
            this.ticket.history.push(history);
          }
          this._save(this.ticket);
        }
      });
    });
  }

  selectRoom() {
    const areas = this.ticket.area ? [this.ticket.area] : [];
    this.modalCtrl.create({
      component: SelectAreaDialogComponent,
      componentProps: { site: this.site(), areas },
      cssClass: ['modal-fullscreen'],
    }).then((m) => {
      m.present();

      m.onDidDismiss<SiteArea>().then((data) => {
        if (data.data) {
          this.ticket.area = { guid: data.data.guid, name: data.data.name };
          this._save(this.ticket);
        }
      });
    });
  }

  onWorker(item: { guid: string; name: string }) {
    this.ticket.users = [item.guid];
    this.ticket.usernames = [item.name];
    const history = this.workerTicketsService.createHistory('assigned', item.name, [], []);
    this.ticket.history.push(history);
    this._save(this.ticket);
  }

  onAdminToWork(_ticket: RoomTicket) {
    this.saveTicket({ status: TicketStatus.created });
  }

  deleteImage(image: string) {
    this.ticket.images = this.ticket.images.filter((img) => img !== image);
    this._save(this.ticket);
  }

  onDelete() {
    Dialog.confirm({
      message: `${this.translate.instant('general.delete') as string} ${this.ticket.name}?`,
      okButtonTitle: this.translate.instant('general.yes'),
      cancelButtonTitle: this.translate.instant('general.no'),
    }).then((dialog) => {
      if (dialog.value) {
        this.workerTicketsService.deleteRoomTicket(this.ticket).then(() => this.dismiss.emit());
      }
    });
  }

  onEditName(ticket: RoomTicket) {
    Dialog.prompt({
      message: this.translate.instant('item.name'),
      inputText: ticket.name,
    }).then((dialogValue) => {
      if (dialogValue) {
        if (!dialogValue.cancelled && dialogValue.value !== ticket.name) {
          ticket.name = dialogValue.value;
          ticket.nameText = null;
          this._save(ticket);
        }
      }
    });
  }

  onEditDescription(ticket: RoomTicket) {
    Dialog.prompt({
      message: this.translate.instant('calendar.site.description'),
      inputText: ticket.description ?? '',
    }).then((dialogValue) => {
      if (dialogValue) {
        if (!dialogValue.cancelled && dialogValue.value !== ticket.description) {
          ticket.description = dialogValue.value;
          ticket.descriptionText = null;
          this._save(ticket);
        }
      }
    });
  }

  onMic(text: string, element: IonInput) {
    const prevText = element.value;
    element.value = prevText ? `${prevText} ${text}` : text;
  }
}
